<template>
  <div>
    <slot name="trigger" v-bind="{ open }">
      <NbButton
        data-ta-tag="nb-modal-trigger-button"
        variant="m"
        color="primary"
        label="Open"
        @click="open"
      />
    </slot>
    <Teleport to="body">
      <transition name="nb-modal-fade" appear>
        <div v-if="isOpen" ref="el" class="nb-modal">
          <div ref="content" class="nb-modal-content" :class="`nb-modal-content-${size}`">
            <div v-if="!headerHidden" class="nb-modal-header nb-text-l-b">
              <slot name="title">
                <span>{{ title }}</span>
              </slot>
              <slot name="close">
                <NbButton
                  v-if="!hideCloseButton"
                  class="nb-modal-close"
                  type="icon"
                  color="ghost"
                  @click="close()"
                />
              </slot>
            </div>
            <div class="nb-modal-body">
              <slot name="body-header">
                <div v-if="contentTitle || contentDescription" class="nb-modal-body-header">
                  <div v-if="contentTitle" class="nb-modal-body-header-title nb-text-m-b">
                    {{ contentTitle }}
                  </div>
                  <div v-if="contentDescription" class="nb-modal-body-header-desc">
                    {{ contentDescription }}
                  </div>
                </div>
              </slot>
              <slot v-bind="{ open, close, isOpen }"> </slot>
              <slot name="alert">
                <div class="nb-modal-body-alert">
                  <NbBannerInline
                    v-if="alertTitle || alertDescription"
                    type="multi"
                    :symbol="negative ? 'info-circle' : 'globe'"
                    :color="negative ? 'negativehigh' : 'neutral'"
                    :text="alertTitle"
                    :description="alertDescription"
                    :close-button="false"
                  />
                </div>
              </slot>
            </div>
            <div
              v-if="!footerHidden"
              class="nb-modal-footer"
              :class="tertiaryButtonText ? '' : 'nb-modal-footer-right'"
            >
              <slot name="footer" v-bind="{ open, close }">
                <NbButton
                  v-if="tertiaryButtonText"
                  data-ta-tag="nb-modal-tertiary-button"
                  variant="m"
                  color="ghost"
                  :label="tertiaryButtonText"
                  :loading="tertiaryLoading"
                  :disabled="
                    tertiaryButtonDisabled || primaryLoading || secondaryLoading || tertiaryLoading
                  "
                  @click="_tertiaryAction"
                />
                <div v-if="secondaryButtonText && primaryButtonText" class="nb-modal-footer-right">
                  <NbButton
                    v-if="secondaryButtonText"
                    data-ta-tag="nb-modal-secondary-button"
                    variant="m"
                    color="ghost"
                    :label="secondaryButtonText"
                    :loading="secondaryLoading"
                    :disabled="
                      secondaryButtonDisabled ||
                      primaryLoading ||
                      secondaryLoading ||
                      tertiaryLoading
                    "
                    @click="_secondaryAction"
                  />
                  <NbButton
                    v-if="primaryButtonText"
                    data-ta-tag="nb-modal-primary-button"
                    :loading="primaryLoading"
                    :color="negative ? 'destructive' : 'primary'"
                    variant="m"
                    :label="primaryButtonText"
                    :disabled="
                      primaryButtonDisabled || primaryLoading || secondaryLoading || tertiaryLoading
                    "
                    @click="_primaryAction"
                  />
                </div>
              </slot>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import '../scss/components/nb-modal.scss'
import type { NbModals } from './NbModals'
import { onClickOutside } from '@vueuse/core'

const el = ref()
const content = ref()
const isOpen = ref(false)
const primaryLoading = ref(false)
const secondaryLoading = ref(false)
const tertiaryLoading = ref(false)

const props = withDefaults(defineProps<NbModals>(), {
  title: '',
  size: 'm',
  persistent: false,
  primaryButtonText: 'Confirm',
  secondaryButtonText: 'Cancel',
  tertiaryButtonText: undefined,
  negative: false,
  closeAfterAction: true,
  hideCloseButton: false
})

const emits = defineEmits<{
  (e: 'open'): void
  (e: 'close'): void
}>()

onClickOutside(content, () => {
  if (!props.persistent) {
    close()
  }
})

const onEscape = (e: KeyboardEvent) => {
  if (isOpen.value && !props.persistent && e.key === 'Escape') {
    close()
  }
}

const open = () => {
  isOpen.value = true
  document.addEventListener('keydown', onEscape)
  emits('open')
}

const close = () => {
  isOpen.value = false
  document.removeEventListener('keydown', onEscape)
  emits('close')
}

const _primaryAction = async () => {
  if (!props.primaryAction) {
    close()
    return
  }
  try {
    primaryLoading.value = true
    await props.primaryAction()
    if (props.closeAfterAction) {
      close()
    }
  } catch (e) {
    console.error(e)
  } finally {
    primaryLoading.value = false
  }
}
const _secondaryAction = async () => {
  if (!props.secondaryAction) {
    close()
    return
  }
  try {
    secondaryLoading.value = true
    await props.secondaryAction()
    if (props.closeAfterAction) {
      close()
    }
  } catch (e) {
    console.error(e)
  } finally {
    secondaryLoading.value = false
  }
}
const _tertiaryAction = async () => {
  if (!props.tertiaryAction) {
    close()
    return
  }
  try {
    tertiaryLoading.value = true
    await props.tertiaryAction()
    if (props.closeAfterAction) {
      close()
    }
  } catch (e) {
    console.error(e)
  } finally {
    tertiaryLoading.value = false
  }
}

defineExpose({
  clickPrimaryButton: _primaryAction,
  clickSecondaryButton: _secondaryAction,
  clickTertiaryButton: _tertiaryAction,
  openModal: open,
  closeModal: close
})
</script>
